<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import CardContainer from '@/components/common/CardContainer.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import LeagueMatchTeamTable from '@/components/widgets/public/LeagueMatchTeamTable.vue'
import LeagueRundownTable from '@/components/widgets/public/LeagueRundownTable.vue'
import { useLeaguesStore } from '@/store/public/leagues'
import { getErrorMessage } from '@/utils/error-handling'

const leagues = useLeaguesStore()
const route = useRoute()

const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)

onMounted(async () => {
  errorMessage.value = null
  isLoading.value = true

  try {
    await leagues.selectRoundById(route.params.round as string)
    await leagues.selectMatchById(route.params.match as string)
    await leagues.fetchLeagueMatchStandings()
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed fetching league standings: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
})
</script>

<template>
  <h1 v-if="isLoading" class="busy-indicator-container">
    <BusyIndicator />
  </h1>

  <NotificationBanner
    v-else-if="errorMessage"
    :message="errorMessage"
    is-danger
  />

  <template v-else>
    <CardContainer>
      <!-- <pre>{{ leagues.selectedRound }}</pre> -->
      <NotificationBanner
        v-if="!leagues.selectedMatch?.standings"
        :message="$t('leagues.errors.no-match-results')"
        is-warning
      />

      <template v-else>
        <div class="row">
          <LeagueMatchTeamTable
            v-if="leagues.selectedMatch?.standings?.home_team"
            class="column"
            :team="leagues.selectedMatch?.standings?.home_team"
          />
          <LeagueMatchTeamTable
            v-if="leagues.selectedMatch?.standings?.away_team"
            class="column"
            :team="leagues.selectedMatch?.standings?.away_team"
          />
        </div>

        <template
          v-if="(leagues.selectedMatch?.standings?.rundown ?? []).length > 0"
        >
          <h4>{{ $t('leagues.labels.rundown') }}</h4>

          <LeagueRundownTable
            :rows="leagues.selectedMatch?.standings?.rundown"
          />
        </template>
      </template>
    </CardContainer>
  </template>
</template>

<style lang="scss" scoped>
.busy-indicator-container {
  text-align: center;
}

.last-updated {
  font-weight: normal;
}

* {
  box-sizing: border-box;
}

.row {
  display: flex;
}

@media screen and (min-width: 768px) {
  .column {
    flex: 50%;
    margin: 5px;
  }
}

@media screen and (max-width: 767px) {
  .column {
    flex: 100%;
    width: 100%;
  }

  .row {
    flex-wrap: wrap;
  }
}
</style>
